import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, ScaleIcon, TrashIcon } from '@heroicons/react/24/outline';
import CourtMandateWarning from './CourtMandateWarning';
import EditImpactPanel from './EditImpactPanel';
import ChangeReasonModal from './ChangeReasonModal';
import { diffTaskLists, flattenPhaseTasks } from './templateDiffUtils';
import {
  getTaskTemplateAdminDetail,
  normalizeTemplatePhases,
  updateTaskTemplateAdmin,
  validateTaskTemplateAdmin,
  type TaskTemplateRecord,
  type TaskTemplateValidationResult,
  type TemplateChangeCategory,
  type TemplateForm,
  type TemplatePhase,
  type TemplateTask,
} from './templateManagementService';

type EditableTask = TemplateTask & { _localId: string };
type EditablePhase = Omit<TemplatePhase, 'tasks'> & { tasks: EditableTask[] };

interface TemplateEditorProps {
  templateId: string;
  onCancel: () => void;
  onSaved?: (templateId: string) => void;
}

interface PendingTaskRemoval {
  phaseIndex: number;
  taskIndex: number;
  task: EditableTask;
}

interface PendingFormRemoval {
  phaseIndex: number;
  taskIndex: number;
  formIndex: number;
  form: TemplateForm;
  task: EditableTask;
}

interface CustomTaskDraft {
  phaseIndex: number;
  title: string;
  description: string;
  due_date_offset_days: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee_role: string;
}

const ASSIGNEE_OPTIONS = ['responsible_attorney', 'originating_attorney', 'responsible_staff', 'creator'];
const PRIORITY_OPTIONS: Array<'low' | 'medium' | 'high' | 'urgent'> = ['low', 'medium', 'high', 'urgent'];

function generateLocalId(): string {
  return `task-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function toEditablePhases(phases: TemplatePhase[]): EditablePhase[] {
  return phases.map((phase) => ({
    ...phase,
    tasks: (phase.tasks || []).map((task) => ({
      ...task,
      _localId: generateLocalId(),
    })),
  }));
}

function buildBaselineTaskMap(phases: EditablePhase[]): Record<string, EditableTask> {
  const map: Record<string, EditableTask> = {};
  for (const phase of phases) {
    for (const task of phase.tasks) {
      map[task._localId] = JSON.parse(JSON.stringify(task)) as EditableTask;
    }
  }
  return map;
}

function toServerPayload(template: TaskTemplateRecord, phases: EditablePhase[]): Partial<TaskTemplateRecord> {
  const serializedPhases: TemplatePhase[] = phases.map((phase) => ({
    id: phase.id,
    name: phase.name,
    description: phase.description,
    order: phase.order,
    tasks: phase.tasks.map(({ _localId: _unusedLocalId, ...task }) => task),
  }));

  const flattenedTasks = serializedPhases.flatMap((phase) => phase.tasks);

  return {
    name: template.name,
    description: template.description,
    practice_area: template.practice_area,
    jurisdiction: template.jurisdiction,
    case_subtype: template.case_subtype,
    parent_template_id: template.parent_template_id,
    phases: serializedPhases,
    tasks: flattenedTasks,
    estimated_duration_days: template.estimated_duration_days,
    complexity_level: template.complexity_level,
    statutory_references: template.statutory_references,
    is_active: template.is_active,
  };
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ templateId, onCancel, onSaved }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [template, setTemplate] = useState<TaskTemplateRecord | null>(null);
  const [phases, setPhases] = useState<EditablePhase[]>([]);
  const [validationResult, setValidationResult] = useState<TaskTemplateValidationResult | null>(null);
  const [pendingTaskRemoval, setPendingTaskRemoval] = useState<PendingTaskRemoval | null>(null);
  const [pendingFormRemoval, setPendingFormRemoval] = useState<PendingFormRemoval | null>(null);
  const baselineTasksRef = useRef<Record<string, EditableTask>>({});
  const baselinePhasesRef = useRef<TemplatePhase[]>([]);
  const [isBaseEdit, setIsBaseEdit] = useState(false);
  const [showChangeReasonModal, setShowChangeReasonModal] = useState(false);
  const [pendingSaveArgs, setPendingSaveArgs] = useState<{ forceRisk: boolean; applyToNewMatters: boolean } | null>(null);
  const [customTaskDraft, setCustomTaskDraft] = useState<CustomTaskDraft>({
    phaseIndex: 0,
    title: '',
    description: '',
    due_date_offset_days: 0,
    priority: 'medium',
    assignee_role: 'responsible_attorney',
  });

  useEffect(() => {
    let mounted = true;
    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getTaskTemplateAdminDetail(templateId);
        if (!mounted) return;

        if (response.template.is_system_template && response.template.template_kind !== 'jurisdiction_base') {
          setError('System templates cannot be edited directly. Create a firm variant first.');
          setLoading(false);
          return;
        }

        const isJurisdictionBase = response.template.is_system_template && response.template.template_kind === 'jurisdiction_base';
        setIsBaseEdit(isJurisdictionBase);

        const normalizedPhases = normalizeTemplatePhases(response.template);
        const editablePhases = toEditablePhases(normalizedPhases);
        baselineTasksRef.current = buildBaselineTaskMap(editablePhases);
        baselinePhasesRef.current = normalizedPhases;
        setTemplate(response.template);
        setPhases(editablePhases);
        setCustomTaskDraft((prev) => ({ ...prev, phaseIndex: 0 }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load template for editing.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTemplate();
    return () => {
      mounted = false;
    };
  }, [templateId]);

  const phaseOptions = useMemo(
    () => phases.map((phase, index) => ({ index, label: `Phase ${phase.order}: ${phase.name}` })),
    [phases],
  );

  const validationSummary = useMemo(() => {
    const result = validationResult;
    if (!result) return { errors: 0, warnings: 0 };
    return {
      errors: result.conflicts.filter((conflict) => conflict.severity === 'error').length,
      warnings:
        result.conflicts.filter((conflict) => conflict.severity === 'warning').length +
        (result.warnings?.length || 0),
    };
  }, [validationResult]);

  const updateTemplateField = (field: keyof TaskTemplateRecord, value: unknown) => {
    if (!template) return;
    setTemplate({ ...template, [field]: value } as TaskTemplateRecord);
    setValidationResult(null);
    setSuccessMessage(null);
  };

  const updateTask = (phaseIndex: number, taskIndex: number, patch: Partial<EditableTask>) => {
    setPhases((prev) =>
      prev.map((phase, pIndex) =>
        pIndex !== phaseIndex
          ? phase
          : {
              ...phase,
              tasks: phase.tasks.map((task, tIndex) => (tIndex !== taskIndex ? task : { ...task, ...patch })),
            },
      ),
    );
    setValidationResult(null);
    setSuccessMessage(null);
  };

  const removeTaskDirect = (phaseIndex: number, taskIndex: number) => {
    setPhases((prev) =>
      prev.map((phase, pIndex) =>
        pIndex !== phaseIndex ? phase : { ...phase, tasks: phase.tasks.filter((_, index) => index !== taskIndex) },
      ),
    );
    setValidationResult(null);
    setSuccessMessage(null);
  };

  const requestTaskRemoval = (phaseIndex: number, taskIndex: number) => {
    const task = phases[phaseIndex]?.tasks[taskIndex];
    if (!task) return;
    const baseline = baselineTasksRef.current[task._localId];
    if (baseline?.is_court_mandated) {
      setPendingTaskRemoval({ phaseIndex, taskIndex, task });
      return;
    }
    removeTaskDirect(phaseIndex, taskIndex);
  };

  const removeFormDirect = (phaseIndex: number, taskIndex: number, formIndex: number) => {
    setPhases((prev) =>
      prev.map((phase, pIndex) =>
        pIndex !== phaseIndex
          ? phase
          : {
              ...phase,
              tasks: phase.tasks.map((task, tIndex) =>
                tIndex !== taskIndex
                  ? task
                  : {
                      ...task,
                      required_forms: (task.required_forms || []).filter((_, index) => index !== formIndex),
                    },
              ),
            },
      ),
    );
    setValidationResult(null);
    setSuccessMessage(null);
  };

  const requestFormRemoval = (phaseIndex: number, taskIndex: number, formIndex: number) => {
    const task = phases[phaseIndex]?.tasks[taskIndex];
    const form = task?.required_forms?.[formIndex];
    if (!task || !form) return;
    if (form.is_mandatory) {
      setPendingFormRemoval({ phaseIndex, taskIndex, formIndex, form, task });
      return;
    }
    removeFormDirect(phaseIndex, taskIndex, formIndex);
  };

  const addCustomTask = () => {
    if (!customTaskDraft.title.trim()) {
      setError('Task title is required to add a custom task.');
      return;
    }

    setPhases((prev) => {
      const phaseIndex = Math.min(Math.max(customTaskDraft.phaseIndex, 0), Math.max(0, prev.length - 1));
      if (prev.length === 0) {
        const fallbackPhase: EditablePhase = {
          name: 'Default Phase',
          order: 1,
          tasks: [],
        };
        const fallbackTask: EditableTask = {
          _localId: generateLocalId(),
          title: customTaskDraft.title.trim(),
          description: customTaskDraft.description.trim() || undefined,
          priority: customTaskDraft.priority,
          due_date_offset_days: customTaskDraft.due_date_offset_days,
          assignee_role: customTaskDraft.assignee_role,
          is_court_mandated: false,
        };
        return [{ ...fallbackPhase, tasks: [fallbackTask] }];
      }

      return prev.map((phase, index) =>
        index !== phaseIndex
          ? phase
          : {
              ...phase,
              tasks: [
                ...phase.tasks,
                {
                  _localId: generateLocalId(),
                  title: customTaskDraft.title.trim(),
                  description: customTaskDraft.description.trim() || undefined,
                  priority: customTaskDraft.priority,
                  due_date_offset_days: customTaskDraft.due_date_offset_days,
                  assignee_role: customTaskDraft.assignee_role,
                  is_court_mandated: false,
                },
              ],
            },
      );
    });

    setCustomTaskDraft((prev) => ({
      ...prev,
      title: '',
      description: '',
      due_date_offset_days: 0,
      priority: 'medium',
      assignee_role: 'responsible_attorney',
    }));
    setError(null);
    setValidationResult(null);
    setSuccessMessage(null);
  };

  const getDeadlineWarning = (task: EditableTask): { message: string; reference: string } | null => {
    const baseline = baselineTasksRef.current[task._localId];
    if (!baseline?.is_court_mandated) return null;

    const baselineDue = baseline.due_date_offset_days;
    const currentDue = task.due_date_offset_days;
    if (baselineDue === currentDue) return null;

    const reference = baseline.mandate_reference || baseline.statutory_deadline?.reference || 'Court mandate';
    const message = `Deadline changed from Day ${baselineDue ?? 'N/A'} to Day ${currentDue ?? 'N/A'} on a court-mandated task.`;
    return { message, reference };
  };

  const revertTaskDeadline = (phaseIndex: number, taskIndex: number) => {
    const task = phases[phaseIndex]?.tasks[taskIndex];
    if (!task) return;
    const baseline = baselineTasksRef.current[task._localId];
    if (!baseline) return;
    updateTask(phaseIndex, taskIndex, { due_date_offset_days: baseline.due_date_offset_days });
  };

  const handleSave = async (forceRisk: boolean, applyToNewMatters: boolean) => {
    if (!template) return;

    // For base edits, intercept to show ChangeReasonModal first
    if (isBaseEdit && !pendingSaveArgs) {
      setPendingSaveArgs({ forceRisk, applyToNewMatters });
      setShowChangeReasonModal(true);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const payload = toServerPayload(template, phases);
      const validation = await validateTaskTemplateAdmin(template.id, payload);
      setValidationResult(validation);

      const hasErrorConflict = validation.conflicts.some((conflict) => conflict.severity === 'error');
      if (hasErrorConflict && !forceRisk) {
        setError('Validation detected court mandate conflicts. Resolve them or use Accept Risk & Save.');
        return;
      }

      const updated = await updateTaskTemplateAdmin(template.id, payload);
      setTemplate(updated);
      setSuccessMessage(
        isBaseEdit
          ? `Base template updated (revision #${updated.revision_number || 'N/A'}).`
          : applyToNewMatters
            ? 'Template saved and marked for use in new matters.'
            : 'Template saved successfully.',
      );
      onSaved?.(updated.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
    } finally {
      setSaving(false);
      setPendingSaveArgs(null);
    }
  };

  const handleChangeReasonConfirm = async (reason: string, category: TemplateChangeCategory) => {
    if (!template || !pendingSaveArgs) return;
    setShowChangeReasonModal(false);

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const payload = {
        ...toServerPayload(template, phases),
        change_reason: reason,
        change_category: category,
      };

      const validation = await validateTaskTemplateAdmin(template.id, payload);
      setValidationResult(validation);

      const hasErrorConflict = validation.conflicts.some((conflict) => conflict.severity === 'error');
      if (hasErrorConflict && !pendingSaveArgs.forceRisk) {
        setError('Validation detected court mandate conflicts. Resolve them or use Accept Risk & Save.');
        return;
      }

      const updated = await updateTaskTemplateAdmin(template.id, payload);
      setTemplate(updated);
      setSuccessMessage(`Base template updated (revision #${updated.revision_number || 'N/A'}).`);
      onSaved?.(updated.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
    } finally {
      setSaving(false);
      setPendingSaveArgs(null);
    }
  };

  const currentTaskDiff = useMemo(() => {
    if (!isBaseEdit) return undefined;
    const baselineTasks = flattenPhaseTasks(baselinePhasesRef.current);
    const currentPhases: TemplatePhase[] = phases.map(p => ({
      ...p,
      tasks: p.tasks.map(({ _localId, ...t }) => t),
    }));
    const currentTasks = flattenPhaseTasks(currentPhases);
    return diffTaskLists(baselineTasks, currentTasks);
  }, [isBaseEdit, phases]);

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        Loading editor...
      </div>
    );
  }

  if (!template) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {error || 'Template could not be loaded for editing.'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Editing: {template.name}
          {isBaseEdit && (
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              Jurisdiction Base
            </span>
          )}
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          {isBaseEdit
            ? 'Editing a jurisdiction base template. Changes will propagate to inheriting variants on next instantiation.'
            : 'Modify tasks for firm-specific workflows while preserving court-mandated requirements.'}
        </p>
      </div>

      {isBaseEdit && <EditImpactPanel templateId={template.id} />}

      {showChangeReasonModal && template && (
        <ChangeReasonModal
          templateName={template.name}
          taskDiff={currentTaskDiff ? {
            added: currentTaskDiff.added,
            removed: currentTaskDiff.removed,
            modified: currentTaskDiff.modified.map(m => m.title),
          } : undefined}
          affectedVariantCount={0}
          onConfirm={handleChangeReasonConfirm}
          onCancel={() => { setShowChangeReasonModal(false); setPendingSaveArgs(null); }}
          isSaving={saving}
        />
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          {successMessage}
        </div>
      )}

      {pendingTaskRemoval && (
        <CourtMandateWarning
          title="Court-Mandated Task Removal Detected"
          description={`"${pendingTaskRemoval.task.title}" is court-mandated. Removing it may create compliance risk.`}
          reference={
            pendingTaskRemoval.task.mandate_reference || pendingTaskRemoval.task.statutory_deadline?.reference || 'Court mandate'
          }
          severity="error"
          onRevert={() => setPendingTaskRemoval(null)}
          onAcceptRisk={() => {
            removeTaskDirect(pendingTaskRemoval.phaseIndex, pendingTaskRemoval.taskIndex);
            setPendingTaskRemoval(null);
          }}
          acceptLabel="Accept Risk & Remove"
        />
      )}

      {pendingFormRemoval && (
        <CourtMandateWarning
          title="Mandatory Form Removal Detected"
          description={`Form "${pendingFormRemoval.form.form_number}" is marked mandatory for "${pendingFormRemoval.task.title}".`}
          reference={pendingFormRemoval.task.mandate_reference || pendingFormRemoval.task.statutory_deadline?.reference}
          severity="warning"
          onRevert={() => setPendingFormRemoval(null)}
          onAcceptRisk={() => {
            removeFormDirect(
              pendingFormRemoval.phaseIndex,
              pendingFormRemoval.taskIndex,
              pendingFormRemoval.formIndex,
            );
            setPendingFormRemoval(null);
          }}
          acceptLabel="Accept Risk & Remove"
        />
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Template Name</span>
            <input
              value={template.name}
              onChange={(event) => updateTemplateField('name', event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Practice Area</span>
            <input
              value={template.practice_area || ''}
              onChange={(event) => updateTemplateField('practice_area', event.target.value || undefined)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
        </div>
        <label className="mt-3 block text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Description</span>
          <textarea
            value={template.description || ''}
            onChange={(event) => updateTemplateField('description', event.target.value || undefined)}
            rows={2}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
      </div>

      <div className="space-y-4">
        {phases.map((phase, phaseIndex) => (
          <section key={`${phase.id || phase.name}-${phaseIndex}`} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Phase {phase.order}: {phase.name}
            </h4>

            <div className="mt-3 space-y-3">
              {phase.tasks.map((task, taskIndex) => {
                const deadlineWarning = getDeadlineWarning(task);
                return (
                  <div key={task._localId} className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <input
                            value={task.title}
                            onChange={(event) => updateTask(phaseIndex, taskIndex, { title: event.target.value })}
                            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                          />
                          {task.is_court_mandated && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-200">
                              <ScaleIcon className="h-3.5 w-3.5" />
                              Mandated
                            </span>
                          )}
                        </div>

                        <textarea
                          value={task.description || ''}
                          onChange={(event) => updateTask(phaseIndex, taskIndex, { description: event.target.value || undefined })}
                          rows={2}
                          placeholder="Task description"
                          className="mt-2 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />

                        <div className="mt-2 grid gap-2 sm:grid-cols-3">
                          <label className="text-xs">
                            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Due Day</span>
                            <input
                              type="number"
                              value={task.due_date_offset_days ?? 0}
                              onChange={(event) =>
                                updateTask(phaseIndex, taskIndex, {
                                  due_date_offset_days: Number.isFinite(Number(event.target.value))
                                    ? Number(event.target.value)
                                    : 0,
                                })
                              }
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            />
                          </label>
                          <label className="text-xs">
                            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Priority</span>
                            <select
                              value={task.priority || 'medium'}
                              onChange={(event) => updateTask(phaseIndex, taskIndex, { priority: event.target.value })}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            >
                              {PRIORITY_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="text-xs">
                            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Assignee</span>
                            <select
                              value={task.assignee_role || 'responsible_attorney'}
                              onChange={(event) => updateTask(phaseIndex, taskIndex, { assignee_role: event.target.value })}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            >
                              {ASSIGNEE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>

                        {task.mandate_reference && (
                          <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">Mandate: {task.mandate_reference}</p>
                        )}

                        {deadlineWarning && (
                          <CourtMandateWarning
                            title="Deadline Change Conflicts With Court Mandate"
                            description={deadlineWarning.message}
                            reference={deadlineWarning.reference}
                            severity="error"
                            onRevert={() => revertTaskDeadline(phaseIndex, taskIndex)}
                            onAcceptRisk={() => handleSave(true, false)}
                          />
                        )}

                        {task.required_forms && task.required_forms.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Required Forms</p>
                            <ul className="mt-1 space-y-1">
                              {task.required_forms.map((form, formIndex) => (
                                <li key={`${form.form_number}-${formIndex}`} className="flex items-center justify-between rounded-md bg-gray-50 px-2 py-1 text-xs dark:bg-gray-900">
                                  <span className="text-gray-700 dark:text-gray-200">
                                    {form.form_number} - {form.form_name}{' '}
                                    {form.is_mandatory ? (
                                      <span className="font-semibold text-rose-700 dark:text-rose-300">(mandatory)</span>
                                    ) : null}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => requestFormRemoval(phaseIndex, taskIndex, formIndex)}
                                    className="text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                                  >
                                    Remove
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => requestTaskRemoval(phaseIndex, taskIndex)}
                        className="rounded-md border border-rose-300 p-1.5 text-rose-600 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/20"
                        title="Remove task"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Add Custom Task</h4>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
          Custom tasks are marked as firm-specific and non-mandated by default.
        </p>
        <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          <label className="text-xs">
            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Phase</span>
            <select
              value={customTaskDraft.phaseIndex}
              onChange={(event) =>
                setCustomTaskDraft((prev) => ({ ...prev, phaseIndex: Number(event.target.value) || 0 }))
              }
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              {phaseOptions.length === 0 ? (
                <option value={0}>Default Phase</option>
              ) : (
                phaseOptions.map((option) => (
                  <option key={option.index} value={option.index}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Title</span>
            <input
              value={customTaskDraft.title}
              onChange={(event) => setCustomTaskDraft((prev) => ({ ...prev, title: event.target.value }))}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Due Day</span>
            <input
              type="number"
              value={customTaskDraft.due_date_offset_days}
              onChange={(event) =>
                setCustomTaskDraft((prev) => ({
                  ...prev,
                  due_date_offset_days: Number.isFinite(Number(event.target.value)) ? Number(event.target.value) : 0,
                }))
              }
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Priority</span>
            <select
              value={customTaskDraft.priority}
              onChange={(event) =>
                setCustomTaskDraft((prev) => ({
                  ...prev,
                  priority: event.target.value as CustomTaskDraft['priority'],
                }))
              }
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              {PRIORITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Assignee</span>
            <select
              value={customTaskDraft.assignee_role}
              onChange={(event) => setCustomTaskDraft((prev) => ({ ...prev, assignee_role: event.target.value }))}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              {ASSIGNEE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="mt-2 block text-xs">
          <span className="mb-1 block font-medium text-gray-600 dark:text-gray-300">Description</span>
          <textarea
            rows={2}
            value={customTaskDraft.description}
            onChange={(event) => setCustomTaskDraft((prev) => ({ ...prev, description: event.target.value }))}
            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
        <button
          type="button"
          onClick={addCustomTask}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
        >
          <PlusIcon className="h-4 w-4" />
          Add Task
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-700 dark:text-gray-200">
            {validationResult ? (
              <span>
                Validation: {validationSummary.errors} errors, {validationSummary.warnings} warnings
              </span>
            ) : (
              <span>Run save to validate mandate conflicts.</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSave(false, false)}
              disabled={saving}
              className="rounded-md border border-lex-accent bg-lex-accent-soft px-3 py-1.5 text-sm font-medium text-lex-accent hover:bg-lex-state-hover disabled:cursor-not-allowed disabled:opacity-60 dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent dark:hover:bg-lex-state-hover"
            >
              Save as Firm Variant
            </button>
            <button
              type="button"
              onClick={() => handleSave(false, true)}
              disabled={saving}
              className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              Save & Apply to New Matters
            </button>
            <button
              type="button"
              onClick={() => handleSave(true, false)}
              disabled={saving}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200 dark:hover:bg-amber-900/30"
            >
              Accept Risk & Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
